import React from 'react';
import cx from 'classnames';

import styles from './Pagination.module.scss';
import Button from '../Button';
import IconButton from '../IconButton';
import ChevronLeftDoubleS1 from '../Icons/ChevronLeftDoubleS1';
import ChevronLeftS1 from '../Icons/ChevronLeftS1';
import ChevronRightDoubleS1 from '../Icons/ChevronRightDoubleS1';
import ChevronRightS1 from '../Icons/ChevronRightS1';
import Overflow from '../Icons/Overflow';

export interface Props {
  className?: Parameters<typeof cx>[0];
  /** The 1-based numerical index of this page within the collection of pages */
  currentPage: number;
  /** The total number of pages to be paginated */
  totalPages: number;
  /** If set to true, used to activate a minimalist layout with only First, Previous, Next, and Last controls. */
  hidePageNumbers?: boolean;
  onPageChange: (arg0: number) => void;
}

interface PageButtonProps {
  /** String to use for the aria-label prop of the page buttons (a11y) */
  ariaLabel: string;
  /** Value to tell the screen reader or other assistive tech that this button
      refers to the current page. */
  ariaCurrent: 'page' | null;
  /** The 1-based numerical index of this page within the collection of pages */
  pageNum: number;
  /** Determines if the current button refers to the currently active page */
  isCurrentPage: boolean;
  /** Action to perform when this button is clicked */
  onClick: () => void;
}

const generatePageButtons = ({
  currentPage = 1,
  totalPages = 1,
  onPageChange = (_: number) => {},
}) => {
  const pageButtons = [];
  let needsLeadingEllipsis = true;
  let needsTrailingEllipsis = true;
  for (let i = 1; i < totalPages + 1; i++) {
    if (totalPages > 7) {
      if (
        [
          currentPage !== 3 && 1,
          currentPage < 4 && 2,
          currentPage < 4 && 3,
          currentPage - 1 >= 0 && currentPage - 1,
          currentPage >= totalPages - 2 && currentPage - 2,
          currentPage >= totalPages - 1 && currentPage - 3,
          currentPage >= totalPages && currentPage - 4,
          currentPage,
          currentPage + 1,
          currentPage - 1 <= 0 && currentPage + 2,
          currentPage <= 3 && totalPages - 2,
          currentPage <= 3 && totalPages - 1,
          totalPages,
        ].indexOf(i) > -1
      ) {
        // If the current page number meets any of the above requirements
        // and there are more than 7 pages, push a numbered button instance
        // into the render queue
        pageButtons.push(
          <PageButton
            key={`page-button-${i}`}
            pageNum={i}
            isCurrentPage={currentPage === i}
            onClick={() => onPageChange(i)}
            ariaCurrent={currentPage === i ? 'page' : null}
            ariaLabel={
              currentPage === i
                ? `Page ${i}`
                : `Go to page ${i} of ${totalPages}`
            }
          />,
        );
      } else {
        // Otherwise, add an ellipsis (...) via the Overflow icon to the render queue
        if (i > 1 && i < currentPage - 1 && needsLeadingEllipsis) {
          pageButtons.push(
            <Overflow className={styles.ellipsis} key={`ellipsis-${i}`} />,
          );
          needsLeadingEllipsis = false;
        } else {
          if (i <= totalPages && i > currentPage + 1 && needsTrailingEllipsis) {
            pageButtons.push(
              <Overflow className={styles.ellipsis} key={`ellipsis-${i}`} />,
            );
            needsTrailingEllipsis = false;
          }
        }
      }
    } else {
      // If there are 7 or fewer pages, push an instance for each number, no
      // ellipses (...) needed.
      pageButtons.push(
        <PageButton
          key={`page-button-${i}`}
          pageNum={i}
          isCurrentPage={currentPage === i}
          onClick={() => onPageChange(i)}
          ariaCurrent={currentPage === i ? 'page' : null}
          ariaLabel={
            currentPage === i ? `Page ${i}` : `Go to page ${i} of ${totalPages}`
          }
        />,
      );
    }
  }
  return pageButtons;
};

const PageButton = ({
  ariaLabel = '',
  ariaCurrent = null,
  pageNum = 1,
  isCurrentPage = false,
  onClick = () => {},
}: PageButtonProps) => {
  return (
    <Button
      small
      centered
      inline
      secondary={!isCurrentPage}
      className={styles.pageButton}
      onClick={onClick}
      ariaLabel={ariaLabel}
      aria-current={ariaCurrent}
      tabIndex={0}
    >
      <span className={styles.visuallyHidden}>Page </span>
      {pageNum}
    </Button>
  );
};

export const Pagination: React.FC<Props> = props => {
  const {
    className,
    onPageChange,
    hidePageNumbers = false,
    currentPage,
    totalPages,
  } = props;

  return (
    <nav aria-label='pagination' className={cx(styles.pagination, className)}>
      <IconButton
        accessibilityLabel={'Go to first page'}
        className={styles.paginationArrowBackward}
        icon={<ChevronLeftDoubleS1 />}
        disabled={currentPage === 1}
        tooltipText={
          currentPage === 1
            ? "You're already on the first page."
            : 'Go to first page'
        }
        onClick={() => {
          onPageChange(1);
        }}
      />
      <IconButton
        accessibilityLabel={'Go to previous page'}
        className={styles.paginationArrowBackward}
        icon={<ChevronLeftS1 />}
        disabled={currentPage === 1}
        tooltipText={
          currentPage === 1
            ? "You're already on the first page."
            : 'Go to previous page'
        }
        onClick={() => {
          onPageChange(currentPage - 1);
        }}
      />
      {!hidePageNumbers && (
        <div className={styles.pageButtonContainer}>
          {generatePageButtons({ currentPage, totalPages, onPageChange })}
        </div>
      )}
      <IconButton
        accessibilityLabel={'Go to next page'}
        className={styles.paginationArrowForward}
        icon={<ChevronRightS1 />}
        disabled={currentPage === totalPages}
        onClick={() => onPageChange(currentPage + 1)}
        tooltipText={
          currentPage === totalPages
            ? "You're already on the last page."
            : 'Go to next page'
        }
      />
      <IconButton
        accessibilityLabel={'Go to last page'}
        className={styles.paginationArrowForward}
        icon={<ChevronRightDoubleS1 />}
        disabled={currentPage === totalPages}
        tooltipText={
          currentPage === totalPages
            ? "You're already on the last page."
            : 'Go to last page'
        }
        onClick={() => onPageChange(totalPages)}
      />
    </nav>
  );
};

Pagination.displayName = 'Pagination';
export default Pagination;
export { styles };
